import { useEffect, useState } from 'react';
import { useAuth } from '@aimsos/auth';
import { ShellAuthProvider } from '@aimsos/auth';
import { useTranslation } from '@aimsos/i18n';
import { GovernanceApp } from '@aimsos/governance';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Governance application inside the Core Shell.
 *
 * Responsibilities:
 * 1. Read the backend JWT from the unified useAuth() — backed by our own
 *    JWT, not Auth0 SDK's per-origin session, so this still mounts
 *    correctly on a {slug}.{rootDomain} subdomain landed on via a
 *    cross-subdomain handoff (see @aimsos/auth's tokenHandoff.js).
 * 2. Set window.__AIMS_SHELL__ so GovernanceApp knows it is embedded.
 * 3. Wrap GovernanceApp with ShellAuthProvider so it can read token/user via context.
 */
const GovernanceEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [govShellI18nReady, setGovShellI18nReady] = useState(false);
  const [govAppI18nReady, setGovAppI18nReady] = useState(false);
  const { t } = useTranslation('governance');

  /** Namespace `governance`: textos del shell (carga de token). */
  useEffect(() => {
    let cancelled = false;
    import('../i18n/registerGovernance.js')
      .then(({ registerGovernanceI18n }) => {
        registerGovernanceI18n(registerNamespace);
        return i18n.loadNamespaces('governance');
      })
      .then(() => {
        if (!cancelled) setGovShellI18nReady(true);
      })
      .catch((err) => console.error('[GovernanceEmbed] shell i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  /** Namespace `governance-app`: strings del paquete @aimsos/governance (mismo i18n que la shell). */
  useEffect(() => {
    let cancelled = false;
    import('@aimsos/governance/i18n/register')
      .then(({ registerGovernanceAppI18n }) => {
        registerGovernanceAppI18n(registerNamespace);
        return i18n.loadNamespaces('governance-app');
      })
      .then(() => {
        if (!cancelled) setGovAppI18nReady(true);
      })
      .catch((err) => console.error('[GovernanceEmbed] governance-app i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlGS: import.meta.env.VITE_API_BASE_URL_GS,
      // Per-aggregate V0 gateways (native V0 — Governance talks to V0 directly,
      // no flag and no V1 fallback). Each base is required.
      apiBaseUrlBundle: import.meta.env.VITE_API_BASE_URL_BUNDLE,
      apiBaseUrlClaim: import.meta.env.VITE_API_BASE_URL_CLAIM,
      apiBaseUrlSandbox: import.meta.env.VITE_API_BASE_URL_SANDBOX,
      apiBaseUrlTruth: import.meta.env.VITE_API_BASE_URL_TRUTH,
      apiBaseUrlUser: import.meta.env.VITE_API_BASE_URL_USER,
      // vault-api serves vault, drives, folders and documents (CT-5403/CT-5402).
      apiBaseUrlVault: import.meta.env.VITE_API_BASE_URL_VAULT,
      // workspace-api serves the Department domain (CT-5404).
      apiBaseUrlWorkspace: import.meta.env.VITE_API_BASE_URL_WORKSPACE,
      // knowledgebase-api serves the Truth Plane / knowledgebase domain (CT-5401).
      apiBaseUrlKnowledgebase: import.meta.env.VITE_API_BASE_URL_KNOWLEDGEBASE,
      // storage-api serves file upload/create (CT-4433; separate gateway, not vault-api).
      apiBaseUrlStorage: import.meta.env.VITE_API_BASE_URL_STORAGE,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token) {
    if (!govShellI18nReady) return null;
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-content-muted">
        {t('embed.loadingToken')}
      </div>
    );
  }

  if (!govAppI18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <GovernanceApp />
    </ShellAuthProvider>
  );
};

export default GovernanceEmbed;
